import React, { useEffect, useRef, useState } from 'react';
import QRCode from 'qrcode';
import { auth } from '../../firebase';
import {
  createInvisibleRecaptcha,
  startSmsEnrollment,
  finishSmsEnrollment,
  startTotpEnrollment,
  finishTotpEnrollment,
  toE164US,
  friendlyAuthError,
} from '../../services/careAuthService';
import './Security.css';

const RECAPTCHA_ID = 'mfa-enroll-recaptcha';

/**
 * Enroll a second factor on the signed-in account. SMS is the default path
 * (external view-only users); an authenticator app (TOTP) is optional.
 *
 * @param {object} p
 * @param {() => Promise<void>|void} p.onEnrolled  called after a factor is enrolled
 * @param {() => void} [p.onCancel]
 * @param {'choose'|'sms'|'totp'} [p.initialStep]
 */
export default function MfaEnrollmentPanel({ onEnrolled, onCancel, initialStep = 'choose' }) {
  const [step, setStep] = useState(initialStep); // choose | sms | sms-code | totp | totp-code
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [verificationId, setVerificationId] = useState(null);
  const [totp, setTotp] = useState(null); // { secret, otpauthUrl, qrDataUrl }
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const recaptchaRef = useRef(null);

  useEffect(() => () => {
    try { recaptchaRef.current?.clear(); } catch { /* ignore */ }
  }, []);

  const getRecaptcha = () => {
    if (!recaptchaRef.current) recaptchaRef.current = createInvisibleRecaptcha(RECAPTCHA_ID);
    return recaptchaRef.current;
  };

  const resetRecaptcha = () => {
    try { recaptchaRef.current?.clear(); } catch { /* ignore */ }
    recaptchaRef.current = null;
  };

  const sendSms = async (e) => {
    e?.preventDefault();
    setError('');
    const e164 = toE164US(phone);
    if (!e164) {
      setError('Enter a valid mobile number (10 digits for US).');
      return;
    }
    setBusy(true);
    try {
      const id = await startSmsEnrollment(e164, getRecaptcha());
      setVerificationId(id);
      setCode('');
      setStep('sms-code');
    } catch (err) {
      resetRecaptcha();
      setError(friendlyAuthError(err, 'Could not send the text message.'));
    } finally {
      setBusy(false);
    }
  };

  const confirmSms = async (e) => {
    e.preventDefault();
    setError('');
    if (code.trim().length < 6) {
      setError('Enter the 6-digit code from the text message.');
      return;
    }
    setBusy(true);
    try {
      const label = `Text to ${toE164US(phone)?.slice(-4) ? `•••• ${toE164US(phone).slice(-4)}` : 'phone'}`;
      await finishSmsEnrollment(verificationId, code, label);
      await onEnrolled?.('phone');
    } catch (err) {
      setError(friendlyAuthError(err, 'Could not verify the code.'));
    } finally {
      setBusy(false);
    }
  };

  const beginTotp = async () => {
    setError('');
    setBusy(true);
    try {
      const { secret, otpauthUrl } = await startTotpEnrollment(auth.currentUser?.email);
      const qrDataUrl = await QRCode.toDataURL(otpauthUrl, { margin: 1, width: 180 });
      setTotp({ secret, otpauthUrl, qrDataUrl });
      setCode('');
      setStep('totp-code');
    } catch (err) {
      setError(friendlyAuthError(err, 'Could not start authenticator setup.'));
    } finally {
      setBusy(false);
    }
  };

  const confirmTotp = async (e) => {
    e.preventDefault();
    setError('');
    if (code.trim().length < 6) {
      setError('Enter the 6-digit code from your authenticator app.');
      return;
    }
    setBusy(true);
    try {
      await finishTotpEnrollment(totp.secret, code);
      await onEnrolled?.('totp');
    } catch (err) {
      setError(friendlyAuthError(err, 'Could not verify the code.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      {error && <div className="sec-error" role="alert">{error}</div>}
      <div id={RECAPTCHA_ID} />

      {step === 'choose' && (
        <div className="sec-methods">
          <button type="button" className="sec-method" onClick={() => setStep('sms')}>
            <div>
              <strong>Text message (SMS)</strong>
              <span>We text a 6-digit code to your mobile phone each time you sign in.</span>
            </div>
            <em className="sec-method-badge">Recommended</em>
          </button>
          <button type="button" className="sec-method" onClick={beginTotp} disabled={busy}>
            <div>
              <strong>Authenticator app</strong>
              <span>Use Microsoft Authenticator, Google Authenticator, Duo Mobile, or 1Password to generate codes.</span>
            </div>
          </button>
          {onCancel && (
            <div className="sec-footer">
              <button type="button" className="sec-link sec-link--muted" onClick={onCancel}>Cancel</button>
            </div>
          )}
        </div>
      )}

      {step === 'sms' && (
        <form onSubmit={sendSms} className="sec-form">
          <label className="sec-label">
            Mobile phone number
            <input
              className="sec-input"
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              placeholder="(555) 555-1234"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              required
              autoFocus
            />
          </label>
          <div className="sec-actions">
            <button type="submit" className="sec-btn sec-btn--primary" disabled={busy}>
              {busy ? 'Sending…' : 'Text me a code'}
            </button>
            <button type="button" className="sec-btn sec-btn--secondary" onClick={() => { setStep('choose'); setError(''); }} disabled={busy}>
              Back
            </button>
          </div>
        </form>
      )}

      {step === 'sms-code' && (
        <form onSubmit={confirmSms} className="sec-form">
          <p className="sec-desc">Enter the 6-digit code we texted to {toE164US(phone)}.</p>
          <label className="sec-label">
            Verification code
            <input
              className="sec-input sec-input--code"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]*"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              required
              autoFocus
            />
          </label>
          <div className="sec-actions">
            <button type="submit" className="sec-btn sec-btn--primary" disabled={busy}>
              {busy ? 'Verifying…' : 'Verify and finish'}
            </button>
            <button type="button" className="sec-btn sec-btn--secondary" onClick={sendSms} disabled={busy}>
              Resend code
            </button>
          </div>
          <button type="button" className="sec-link sec-link--muted" onClick={() => { setStep('sms'); setError(''); }}>
            Use a different number
          </button>
        </form>
      )}

      {step === 'totp-code' && totp && (
        <form onSubmit={confirmTotp} className="sec-form">
          <p className="sec-desc">
            Open your authenticator app, choose <strong>Add account</strong> / <strong>Scan QR code</strong>, and scan this code.
            Then enter the 6-digit code the app shows.
          </p>
          <div className="sec-qr">
            <img src={totp.qrDataUrl} alt="Authenticator QR code" />
            <details>
              <summary className="sec-link sec-link--muted">Can't scan? Enter the key manually</summary>
              <div className="sec-secret">{totp.secret.secretKey}</div>
            </details>
          </div>
          <label className="sec-label">
            6-digit code
            <input
              className="sec-input sec-input--code"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]*"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              required
              autoFocus
            />
          </label>
          <div className="sec-actions">
            <button type="submit" className="sec-btn sec-btn--primary" disabled={busy}>
              {busy ? 'Verifying…' : 'Verify and finish'}
            </button>
            <button type="button" className="sec-btn sec-btn--secondary" onClick={() => { setStep('choose'); setTotp(null); setError(''); }} disabled={busy}>
              Back
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
